import type { Database } from 'sql.js';

let db: Database | null = null;

export interface FavoriteModel {
  id?: number;
  name: string;
  description: string;
  created_at: string;
  usage_count: number;
}

export interface CustomModel {
  id?: number;
  name: string;
  description: string;
  base_colors: string;
  created_at: string;
}

export interface FavoritePalette {
  id?: number;
  name: string;
  colors: string[];
  model?: string;
  created_at: string;
}

export const initDatabase = async (): Promise<void> => {
  try {
    const sqlJs = await import('sql.js');
    const initSqlJs = (sqlJs.default as any).initSqlJs || sqlJs.default;
    
    const SQL = await initSqlJs({
      locateFile: (file) => {
        // Use different paths for development vs production
        if (import.meta.env.DEV) {
          return `/${file}`;
        } else {
          // In production, try to load from the same domain first, fallback to CDN
          return `/${file}`;
        }
      }
    });
    
    // Try to load existing database from localStorage
    const savedDb = localStorage.getItem('colormind_db');
    if (savedDb) {
      try {
        const uint8Array = new Uint8Array(JSON.parse(savedDb));
        db = new SQL.Database(uint8Array);
      } catch (error) {
        console.warn('Failed to load saved database, creating new one:', error);
        db = new SQL.Database();
      }
    } else {
      db = new SQL.Database();
    }

    // Create tables if they don't exist
    db.run(`
      CREATE TABLE IF NOT EXISTS favorite_models (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT UNIQUE NOT NULL,
        description TEXT NOT NULL,
        created_at TEXT NOT NULL,
        usage_count INTEGER DEFAULT 0
      )
    `);

    db.run(`
      CREATE TABLE IF NOT EXISTS custom_models (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT UNIQUE NOT NULL,
        description TEXT NOT NULL,
        base_colors TEXT NOT NULL,
        created_at TEXT NOT NULL
      )
    `);

    db.run(`
      CREATE TABLE IF NOT EXISTS favorite_palettes (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        name TEXT NOT NULL,
        colors TEXT NOT NULL,
        model TEXT,
        created_at TEXT NOT NULL
      )
    `);

    console.log('Database initialized successfully');
  } catch (error) {
    console.error('Failed to initialize database:', error);
    // Graceful fallback - app will work without database features
    console.warn('App will continue without database features (favorites, etc.)');
  }
};

export const saveDatabase = (): void => {
  if (db) {
    try {
      const data = db.export();
      const buffer = JSON.stringify(Array.from(data));
      localStorage.setItem('colormind_db', buffer);
    } catch (error) {
      console.error('Failed to save database:', error);
    }
  }
};

// Favorite Models functions
export const addFavoriteModel = (name: string, description: string): boolean => {
  if (!db) return false;
  
  try {
    db.run(
      'INSERT OR REPLACE INTO favorite_models (name, description, created_at, usage_count) VALUES (?, ?, ?, COALESCE((SELECT usage_count FROM favorite_models WHERE name = ?), 0))',
      [name, description, new Date().toISOString(), name]
    );
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to add favorite model:', error);
    return false;
  }
};

export const removeFavoriteModel = (name: string): boolean => {
  if (!db) return false;
  
  try {
    db.run('DELETE FROM favorite_models WHERE name = ?', [name]);
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to remove favorite model:', error);
    return false;
  }
};

export const getFavoriteModels = (): FavoriteModel[] => {
  if (!db) return [];
  
  try {
    const stmt = db.prepare('SELECT * FROM favorite_models ORDER BY usage_count DESC, created_at DESC');
    const results: FavoriteModel[] = [];
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push({
        id: row.id as number,
        name: row.name as string,
        description: row.description as string,
        created_at: row.created_at as string,
        usage_count: row.usage_count as number
      });
    }
    
    stmt.free();
    return results;
  } catch (error) {
    console.error('Failed to get favorite models:', error);
    return [];
  }
};

export const incrementModelUsage = (name: string): void => {
  if (!db) return;
  
  try {
    db.run('UPDATE favorite_models SET usage_count = usage_count + 1 WHERE name = ?', [name]);
    saveDatabase();
  } catch (error) {
    console.error('Failed to increment model usage:', error);
  }
};

// Custom Models functions
export const addCustomModel = (name: string, description: string, baseColors: string[]): boolean => {
  if (!db) return false;
  
  try {
    db.run(
      'INSERT INTO custom_models (name, description, base_colors, created_at) VALUES (?, ?, ?, ?)',
      [name, description, JSON.stringify(baseColors), new Date().toISOString()]
    );
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to add custom model:', error);
    return false;
  }
};

export const removeCustomModel = (id: number): boolean => {
  if (!db) return false;
  
  try {
    db.run('DELETE FROM custom_models WHERE id = ?', [id]);
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to remove custom model:', error);
    return false;
  }
};

export const getCustomModels = (): CustomModel[] => {
  if (!db) return [];
  
  try {
    const stmt = db.prepare('SELECT * FROM custom_models ORDER BY created_at DESC');
    const results: CustomModel[] = [];
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push({
        id: row.id as number,
        name: row.name as string,
        description: row.description as string,
        base_colors: row.base_colors as string,
        created_at: row.created_at as string
      });
    }
    
    stmt.free();
    return results;
  } catch (error) {
    console.error('Failed to get custom models:', error);
    return [];
  }
};

// Favorite Palettes functions
export const addFavoritePalette = (name: string, colors: string[], model?: string): boolean => {
  if (!db) return false;
  
  try {
    db.run(
      'INSERT INTO favorite_palettes (name, colors, model, created_at) VALUES (?, ?, ?, ?)',
      [name, JSON.stringify(colors), model || null, new Date().toISOString()]
    );
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to add favorite palette:', error);
    return false;
  }
};

export const removeFavoritePalette = (id: number): boolean => {
  if (!db) return false;
  
  try {
    db.run('DELETE FROM favorite_palettes WHERE id = ?', [id]);
    saveDatabase();
    return true;
  } catch (error) {
    console.error('Failed to remove favorite palette:', error);
    return false;
  }
};

export const getFavoritePalettes = (): FavoritePalette[] => {
  if (!db) return [];
  
  try {
    const stmt = db.prepare('SELECT * FROM favorite_palettes ORDER BY created_at DESC');
    const results: FavoritePalette[] = [];
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      results.push({
        id: row.id as number,
        name: row.name as string,
        colors: JSON.parse(row.colors as string),
        model: row.model as string || undefined,
        created_at: row.created_at as string
      });
    }
    
    stmt.free();
    return results;
  } catch (error) {
    console.error('Failed to get favorite palettes:', error);
    return [];
  }
};